/**
 * Icon trays for the video tools' settings — the same shape as the image and
 * PDF tools' trays (a tinted strip of tiles, the picked one lifted white), in
 * the video category's violet. Glyphs draw in `currentColor`, so a tile's
 * active / idle text colour paints them too.
 */

/** The strip behind a row of tiles; pass the grid-cols class for its row. */
export function videoTrayClass(colsClass) {
    return `grid gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800 ${colsClass}`;
}

/** One tile: glyph, optionally over a short label. */
export function videoTileClass(active) {
    return `flex min-h-[54px] min-w-0 flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-violet-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-violet-400'
            : 'bg-transparent font-medium text-[#6B635C] hover:bg-white/60 hover:text-violet-600 dark:text-zinc-400 dark:hover:bg-zinc-900/60'
    }`;
}

/** A frame with the mark sitting where it will land on the video. */
export function PositionGlyph({ position }) {
    const spot = {
        center: [12, 12],
        'top-left': [7.5, 7.5],
        'top-right': [16.5, 7.5],
        'bottom-left': [7.5, 16.5],
        'bottom-right': [16.5, 16.5],
    }[position] ?? [12, 12];
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <rect x={spot[0] - 3} y={spot[1] - 2} width="6" height="4" rx="1" fill="currentColor" />
        </svg>
    );
}

/**
 * A dial whose needle leans by speed: straight up at 1×, a fixed step left or
 * right per halving or doubling, so 0.5× and 2× mirror each other.
 */
export function SpeedGlyph({ value }) {
    const angle = Math.max(-80, Math.min(80, Math.log2(Number(value) || 1) * 65));
    const rad = (angle * Math.PI) / 180;
    const x = 12 + Math.sin(rad) * 7;
    const y = 15 - Math.cos(rad) * 7;
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 17a8 8 0 1 1 16 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="12" y1="15" x2={x} y2={y} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="12" cy="15" r="1.6" fill="currentColor" />
        </svg>
    );
}

/**
 * Output width as a frame drawn to scale against the widest preset; the
 * original size is the full frame with corner marks.
 */
export function WidthGlyph({ width, maxWidth = 640 }) {
    if (!width) {
        return (
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="6" width="18" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
                <path d="M6 9.5V9h.5M18 9.5V9h-.5M6 14.5v.5h.5M18 14.5v.5h-.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
        );
    }
    const w = 5 + 16 * Math.min(1, width / maxWidth);
    const h = w * 0.66;
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect x={12 - w / 2} y={12 - h / 2} width={w} height={h} rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    );
}

const BAYER_4 = [
    [0, 8, 2, 10],
    [12, 4, 14, 6],
    [3, 11, 1, 9],
    [15, 7, 13, 5],
];

/** Deterministic 0–1 noise per cell, so the "diffused" glyphs never flicker. */
function cellNoise(col, row, seed) {
    const n = Math.sin((col + 1) * 12.9898 + (row + 1) * 78.233 + seed * 37.719) * 43758.5453;
    return n - Math.floor(n);
}

/**
 * A left-to-right fade drawn the way each method draws it: scattered dots for
 * the two error-diffusion methods, a regular grid for Bayer, and a hard edge
 * for no dithering.
 */
export function DitherGlyph({ method }) {
    const cells = method === 'sierra2_4a' ? 5 : 6;
    const size = 20;
    const cell = size / cells;
    const dots = [];
    for (let row = 0; row < cells; row++) {
        for (let col = 0; col < cells; col++) {
            const level = 1 - (col + 0.5) / cells;
            const on = method === 'none'
                ? level > 0.5
                : method === 'bayer'
                    ? (BAYER_4[row % 4][col % 4] + 0.5) / 16 < level
                    : cellNoise(col, row, method === 'sierra2_4a' ? 2 : 1) < level;
            if (on) {
                dots.push(
                    <rect
                        key={`${row}-${col}`}
                        x={2 + col * cell + cell * 0.12}
                        y={2 + row * cell + cell * 0.12}
                        width={cell * 0.76}
                        height={cell * 0.76}
                        rx={method === 'none' ? 0 : cell * 0.3}
                        fill="currentColor"
                    />,
                );
            }
        }
    }
    return (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="1.5" y="1.5" width="21" height="21" rx="3" fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1" />
            {dots}
        </svg>
    );
}
